import { useMemo } from 'react';
import { useSessionStorage } from 'react-use';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { SmartDiaryEntry } from 'modules/smartDiary/smartDiaryNotes/type';

export type UseSmartDiaryTabsArgs = {
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>;
  defaultTabValue: SmartDiaryListTabValue;
};

export type UseSmartDiaryTabsResult = {
  tabValue: SmartDiaryListTabValue;
  setTabValue: (value: SmartDiaryListTabValue) => void;
  notAppliedEntries: Record<string, SmartDiaryEntry[]>;
  notAppliedCount: number;
  appliedEntries: Record<string, SmartDiaryEntry[]>;
  appliedCount: number;
};

type SmartDiaryTabFilterObject = {
  [index in SmartDiaryListTabValue]: DiaryEntrySyncStatus[];
};

export enum SmartDiaryListTabValue {
  NOT_APPLIED = 'NOT_APPLIED',
  APPLIED = 'APPLIED',
  ALL = 'ALL'
}

const tabFilters: SmartDiaryTabFilterObject = {
  [SmartDiaryListTabValue.NOT_APPLIED]: [
    DiaryEntrySyncStatus.NotSynced,
    DiaryEntrySyncStatus.DeletePending,
    DiaryEntrySyncStatus.UpdatePending
  ],
  [SmartDiaryListTabValue.APPLIED]: [DiaryEntrySyncStatus.Synced],
  [SmartDiaryListTabValue.ALL]: [
    DiaryEntrySyncStatus.NotSynced,
    DiaryEntrySyncStatus.DeletePending,
    DiaryEntrySyncStatus.UpdatePending,
    DiaryEntrySyncStatus.Synced
  ]
};

export const filterSmartEntries = (
  smartEntries: SmartDiaryEntry[],
  tabValue: SmartDiaryListTabValue
): SmartDiaryEntry[] => {
  return smartEntries.filter((smartEntry: SmartDiaryEntry) =>
    smartEntry.syncStatus
      ? tabFilters[tabValue].includes(smartEntry.syncStatus)
      : false
  );
};

export const getSmartEntryCount = (
  smartEntriesMap: Record<string, SmartDiaryEntry[]>
): number => {
  const flattenedSmartEntries = Object.values(smartEntriesMap).flat();

  return flattenedSmartEntries.length;
};

export const useSmartDiaryTabs = ({
  smartEntriesByDay,
  defaultTabValue
}: UseSmartDiaryTabsArgs): UseSmartDiaryTabsResult => {
  const me = useMeContext();
  const [tabValue, setTabValue] = useSessionStorage(
    `${me?.id}_tabSelection`,
    defaultTabValue
  );

  const notAppliedEntries = useMemo(
    () =>
      Object.keys(smartEntriesByDay).reduce((acc, key) => {
        const filteredEntries = filterSmartEntries(
          smartEntriesByDay[key],
          SmartDiaryListTabValue.NOT_APPLIED
        );
        return { ...acc, [key]: filteredEntries };
      }, {} as Record<string, SmartDiaryEntry[]>),
    [smartEntriesByDay]
  );
  const notAppliedCount = useMemo(() => {
    return getSmartEntryCount(notAppliedEntries);
  }, [notAppliedEntries]);

  const appliedEntries = useMemo(
    () =>
      Object.keys(smartEntriesByDay).reduce((acc, key) => {
        const filteredEntries = filterSmartEntries(
          smartEntriesByDay[key],
          SmartDiaryListTabValue.APPLIED
        );
        return { ...acc, [key]: filteredEntries };
      }, {} as Record<string, SmartDiaryEntry[]>),
    [smartEntriesByDay]
  );
  const appliedCount = useMemo(() => {
    return getSmartEntryCount(appliedEntries);
  }, [appliedEntries]);

  return useMemo(
    () => ({
      tabValue,
      setTabValue,
      notAppliedEntries,
      notAppliedCount,
      appliedEntries,
      appliedCount
    }),
    [
      tabValue,
      setTabValue,
      notAppliedEntries,
      notAppliedCount,
      appliedEntries,
      appliedCount
    ]
  );
};
